// One id per club, whatever the source calls it.
//
// Three feeds spell the same team three ways: The Odds API uses the nickname
// ("Collingwood Magpies"), Betfair's CSVs use the short name ("Collingwood"),
// Squiggle uses the club name ("Collingwood"). Nothing downstream can join a
// price to a result until they all collapse to one id, so everything that
// names a team goes through `teamId` first.

import { localDate } from '../tickets.js';
import aflTeams from './teams/afl.json' with { type: 'json' };
import nrlTeams from './teams/nrl.json' with { type: 'json' };
import aleagueTeams from './teams/aleague.json' with { type: 'json' };
import nblTeams from './teams/nbl.json' with { type: 'json' };
import bblTeams from './teams/bbl.json' with { type: 'json' };
import eplTeams from './teams/epl.json' with { type: 'json' };
import mlbTeams from './teams/mlb.json' with { type: 'json' };

// `suffixes` are the generic tails a feed bolts onto a club name. Stripping
// them is the fallback when the alias table has no entry: a new club, or a
// nickname nobody wrote down, still lands on the short id.
export const SPORTS = {
  afl: {
    tz: 'Australia/Sydney',
    oddsApiKeys: ['aussierules_afl'],
    suffixes: [
      'crows', 'lions', 'blues', 'magpies', 'bombers', 'dockers', 'cats', 'suns',
      'giants', 'hawks', 'demons', 'kangaroos', 'power', 'tigers', 'saints',
      'swans', 'eagles', 'bulldogs',
    ],
  },
  nrl: {
    tz: 'Australia/Sydney',
    oddsApiKeys: ['rugbyleague_nrl'],
    suffixes: [
      'broncos', 'raiders', 'bulldogs', 'sharks', 'titans', 'sea eagles', 'storm',
      'knights', 'cowboys', 'eels', 'panthers', 'rabbitohs', 'dragons', 'roosters',
      'warriors', 'tigers',
    ],
  },
  aleague: {
    tz: 'Australia/Sydney',
    oddsApiKeys: ['soccer_australia_aleague'],
    suffixes: ['fc'],
  },
  nbl: {
    tz: 'Australia/Sydney',
    oddsApiKeys: ['basketball_nbl'],
    suffixes: [],
  },
  bbl: {
    tz: 'Australia/Sydney',
    oddsApiKeys: ['cricket_big_bash'],
    suffixes: [],
  },
  epl: {
    tz: 'Europe/London',
    oddsApiKeys: ['soccer_epl'],
    suffixes: ['fc', 'afc'],
  },
  // MLB. The app has been pricing baseball for months WITHOUT a row here, which
  // is what `APP_TZ` below is about: every key was dated in Sydney, so a 22:41Z
  // first pitch belonged to a Sydney day that is not the day anybody in America
  // calls it. `tz` makes the date the one the competition uses.
  //
  // THIS CHANGES THE KEY OF EVERY MLB LINE from the day it lands: the sport id
  // goes from the provider's own `baseball_mlb` (what `identify` falls back to
  // for a sport the registry does not carry) to `mlb`, and the date from the
  // Sydney one to the New York one. Rows already stored keep the old key for
  // ever; see the `--rekey` note in scripts/ingest-mlb-history.mjs and the F6
  // section of HANDOFF.md. Settlement itself is safe either way — it joins on
  // the provider's event id first (see settlement.js) — and nothing else in the
  // app reads an MLB key across the boundary.
  //
  // No `suffixes`: a baseball club's tail IS its nickname ("Red Sox"), and
  // stripping it would turn two Chicago clubs into one city.
  mlb: {
    tz: 'America/New_York',
    oddsApiKeys: ['baseball_mlb'],
    suffixes: [],
  },
  // THE THREE RACING CODES. A race has no clubs, so there is no alias table to
  // keep: a horse is named once, by the form guide, and the slug of that name
  // is its id. `oddsApiKeys` is empty because racing never comes from The Odds
  // API — it is PuntersEdge and the exchange — and an empty list is also what
  // keeps `sportFromOddsApiKey` from ever answering one of these.
  racing_horse: { tz: 'Australia/Sydney', oddsApiKeys: [], suffixes: [] },
  racing_greyhound: { tz: 'Australia/Sydney', oddsApiKeys: [], suffixes: [] },
  racing_harness: { tz: 'Australia/Sydney', oddsApiKeys: [], suffixes: [] },
  // CRICKET BY FORMAT, because the toss is a different animal in each one and a
  // model fitted across all three would be fitting the average of three
  // sports. The Big Bash keeps its own id above (`bbl`, with an alias table and
  // a board the app already prices); everything else the feed offers lands on
  // the format it is played in, and the history Cricsheet writes is filed the
  // same way (scripts/ingest-cricsheet.mjs).
  //
  // No alias table: cricket is hundreds of clubs and every Test nation, and an
  // empty table is what makes `teamId('cricket_odi', 'Sri Lanka')` answer
  // 'sri-lanka' rather than null. The zone is Sydney because these are keys
  // dated in the app's own day (`APP_TZ` below), not in Colombo's.
  cricket_t20: {
    tz: 'Australia/Sydney',
    oddsApiKeys: [
      'cricket_international_t20', 'cricket_ipl', 'cricket_t20_blast',
      'cricket_caribbean_premier_league', 'cricket_psl', 'cricket_the_hundred',
      'cricket_t20_world_cup',
    ],
    suffixes: [],
  },
  cricket_odi: {
    tz: 'Australia/Sydney',
    oddsApiKeys: ['cricket_odi', 'cricket_icc_world_cup', 'cricket_asia_cup', 'cricket_icc_trophy'],
    suffixes: [],
  },
  cricket_test: { tz: 'Australia/Sydney', oddsApiKeys: ['cricket_test_match'], suffixes: [] },
};

/** The cricket sport ids, the Big Bash included. One list, so nothing keeps a second. */
export const CRICKET_SPORTS = ['bbl', 'cricket_t20', 'cricket_odi', 'cricket_test'];

/** The sports that are races: a field of runners, not two teams and a draw. */
export const RACING_SPORTS = ['racing_horse', 'racing_greyhound', 'racing_harness'];

/** Is this sport id a racing code? One list, so nothing can carry a second. */
export const isRacingSport = (sport) => RACING_SPORTS.includes(sport);

/**
 * What a sport is called on screen. The keys of `SPORTS` are ids and read like
 * ids ("aleague"); a tick-box in the strategy editor has to say "A-League".
 * An id with no entry falls back to its own uppercase, so a sport added to
 * SPORTS and not to this map is legible rather than missing.
 */
export const SPORT_LABELS = {
  afl: 'AFL',
  nrl: 'NRL',
  aleague: 'A-League',
  nbl: 'NBL',
  bbl: 'BBL',
  epl: 'Premier League',
  mlb: 'MLB',
  racing_horse: 'Horses',
  racing_greyhound: 'Greyhounds',
  racing_harness: 'Harness',
  cricket_t20: 'T20',
  cricket_odi: 'ODI',
  cricket_test: 'Test cricket',
};

/** The on-screen name for a sport id. */
export const sportLabel = (id) => SPORT_LABELS[id] ?? String(id ?? '').toUpperCase();

const TEAMS = {
  afl: aflTeams,
  nrl: nrlTeams,
  aleague: aleagueTeams,
  nbl: nblTeams,
  bbl: bblTeams,
  epl: eplTeams,
  mlb: mlbTeams,
  // An empty table is not the same as no table: it builds an empty index, and
  // an empty index is what makes `teamId('racing_horse', 'Winx')` answer
  // 'winx' rather than null. A sport missing from here is an unknown sport.
  racing_horse: [],
  racing_greyhound: [],
  racing_harness: [],
  cricket_t20: [],
  cricket_odi: [],
  cricket_test: [],
};

/** Lowercase, no diacritics, alphanumerics joined by single hyphens. */
export function slug(name) {
  return String(name ?? '')
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The Odds API's sport key → our sport id; null for a sport we don't carry. */
export function sportFromOddsApiKey(sportKey) {
  for (const [sport, meta] of Object.entries(SPORTS)) {
    if (meta.oddsApiKeys.includes(sportKey)) return sport;
  }
  return null;
}

// slug → id, per sport. Built once; the first claim on a slug wins, so a team's
// own name and id always beat another club's loose nickname.
const INDEX = {};
for (const [sport, teams] of Object.entries(TEAMS)) {
  const index = new Map();
  const claim = (text, id) => {
    const key = slug(text);
    if (key && !index.has(key)) index.set(key, id);
  };
  for (const team of teams) claim(team.id, team.id);
  for (const team of teams) claim(team.name, team.id);
  for (const team of teams) for (const alias of team.aliases ?? []) claim(alias, team.id);
  INDEX[sport] = index;
}

function stripSuffix(sport, slugged) {
  for (const suffix of SPORTS[sport].suffixes) {
    const tail = `-${slug(suffix)}`;
    if (slugged.endsWith(tail) && slugged.length > tail.length) return slugged.slice(0, -tail.length);
  }
  return slugged;
}

/**
 * Canonical team id for a name in a sport, or null when the sport is unknown.
 * An unrecognised team in a known sport keeps its slugged (suffix-stripped)
 * name so ingestion is never silently dropped — `isKnownTeam` is the test to
 * report on.
 */
export function teamId(sport, name) {
  const index = INDEX[sport];
  if (!index) return null;
  const slugged = slug(name);
  if (index.has(slugged)) return index.get(slugged);
  const stripped = stripSuffix(sport, slugged);
  return index.get(stripped) ?? stripped;
}

/** Is this name in the sport's alias table? */
export function isKnownTeam(sport, name) {
  const index = INDEX[sport];
  if (!index) return false;
  const slugged = slug(name);
  return index.has(slugged) || index.has(stripSuffix(sport, slugged));
}

/** Every canonical id in a sport, in table order. */
export function teamIds(sport) {
  return (TEAMS[sport] ?? []).map((t) => t.id);
}

/**
 * THE ZONE A KEY IS DATED IN WHEN THE SPORT DOES NOT NAME ONE. Legwork is an
 * Australian app and its day is the Sydney day: the pad, the paper trail and
 * every `live/{date}.json` on the server are already filed that way.
 *
 * It exists because `SPORTS[sport]?.tz` answers `undefined` for a sport the
 * registry does not carry — MLB, NFL, anything The Odds API offers that nobody
 * has written a row for — and `localDate(when, undefined)` then dates the key
 * in WHOEVER IS ASKING's zone. The phone asked in Sydney and the server asked
 * in UTC, so a 22:41Z game keyed `…:2026-09-15:…` on the board and
 * `…:2026-09-14:…` in the results, and no leg on it could ever be settled.
 * Every US game kicks off inside that window, which is how the whole of an
 * MLB night went unsettled on 2026-09-15.
 *
 * A key is a JOIN, so it may not depend on where it is computed. Anything
 * without its own zone is dated here.
 */
export const APP_TZ = 'Australia/Sydney';

/**
 * The join key for one fixture: sport, local match date, home, away. The date
 * is local to the competition, so a 20:00 Friday kickoff is the Friday's match
 * wherever the reader is sitting — and, for a competition the registry does not
 * know, local to Sydney rather than to the machine (see `APP_TZ`).
 */
export function matchKey(sport, kickoffIso, homeId, awayId, tz = SPORTS[sport]?.tz ?? APP_TZ) {
  return `${sport}:${localDate(kickoffIso, tz)}:${homeId}:${awayId}`;
}

/**
 * The join key for one RACE: code, the meeting's local date, the venue, the
 * race number. `matchKey`'s shape and `matchKey`'s reasons — the date is local
 * to the meeting, so a twilight race that runs at 14:00 UTC belongs to the
 * Sydney day it was carded on wherever the reader is sitting.
 *
 * Venue and number rather than the field, because the field changes: a
 * scratching between two fetches must not make a new race, and a provider's own
 * race id is a provider's, not a join key two of them can meet on.
 */
export function raceKey(sport, kickoffIso, venueSlug, raceNumber, tz = SPORTS[sport]?.tz ?? APP_TZ) {
  return `${sport}:${localDate(kickoffIso, tz)}:${venueSlug}:R${raceNumber}`;
}
